import { ParseError } from './parse-error'

// Business dates are 'YYYY-MM-DD' in Europe/Berlin; timestamps are ISO-8601 UTC (spec §1, conventions).
// This module never reads the clock: every function takes the instant it converts.

const BERLIN_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function toInstant(ts: string | Date): Date {
  const d = ts instanceof Date ? ts : new Date(ts)
  if (Number.isNaN(d.getTime())) throw new ParseError(`timestamp: "${String(ts)}" is not a valid instant`)
  return d
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

// Validates a calendar date and returns it as 'YYYY-MM-DD'.
function calendarDate(y: number, m: number, d: number, raw: string): string {
  const probe = new Date(Date.UTC(y, m - 1, d))
  if (y < 1000 || probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    throw new ParseError(`date: "${raw}" is not a calendar date`)
  }
  return `${y}-${pad2(m)}-${pad2(d)}`
}

export function berlinDate(ts: string | Date): string {
  const parts = BERLIN_PARTS.formatToParts(toInstant(ts))
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

export function berlinYear(ts: string | Date): number {
  return Number(berlinDate(ts).slice(0, 4))
}

const BERLIN_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

// Berlin wall-clock hour and minute of an instant. The cron branches in src/worker/index.ts gate on this:
// there is one trigger (*/5), and "03:00 Berlin" is hour === 3 && minute < 5 on it.
export function berlinTime(ts: string | Date): { hour: number; minute: number } {
  const parts = BERLIN_CLOCK.formatToParts(toInstant(ts))
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? '0')
  return { hour: get('hour'), minute: get('minute') }
}

// Day of the Berlin week: 0 = Sonntag, 1 = Montag … 6 = Samstag. Derived from the Berlin calendar date, so
// 00:30 Berlin on a Monday is a Monday even though it is still Sunday in UTC.
export function berlinWeekday(ts: string | Date): number {
  return new Date(`${berlinDate(ts)}T00:00:00Z`).getUTCDay()
}

const DMY_SLASH = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/ // 5/9/2026 = 5 September (HighLevel right_now.date)
const DMY_DOT = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/ // 04.06.2026
// ISO date, optionally followed by a time part; the calendar date is taken literally (no zone conversion).
const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

// HighLevel / sheet date string → 'YYYY-MM-DD'. Excel serial numbers are NOT accepted here:
// the importer converts them with excelSerialToIso first (spec §1.6).
export function parseHlDate(raw: unknown, format: 'd/m/yyyy'): string | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw !== 'string') {
    throw new ParseError(`date: unsupported value type ${typeof raw} (serial numbers go through excelSerialToIso)`)
  }
  const s = raw.trim()
  if (s === '' || s === '-') return null
  const dayFirst = DMY_SLASH.exec(s) ?? DMY_DOT.exec(s)
  if (dayFirst) {
    return calendarDate(Number(dayFirst[3]), Number(dayFirst[2]), Number(dayFirst[1]), raw)
  }
  const iso = ISO.exec(s)
  if (iso) {
    return calendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]), raw)
  }
  throw new ParseError(`date: "${raw}" matches neither ${format}, dd.mm.yyyy nor ISO`)
}

// Google Sheets / Excel serial (1900 date system, epoch 1899-12-30; 25569 = 1970-01-01) → 'YYYY-MM-DD'.
// A fractional part (time of day) is ignored.
export function excelSerialToIso(serial: number): string {
  if (!Number.isFinite(serial)) throw new ParseError(`serial: ${serial} is not a finite number`)
  const days = Math.floor(serial) - 25_569
  return new Date(days * 86_400_000).toISOString().slice(0, 10)
}
